import type { TypedDocumentNode } from '@apollo/client'
import { gql, NetworkStatus } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  DateFormField,
  EnumSelectFormField,
  InputFormField,
  SliderFormField,
  SwitchFormField,
  TagMultiSelectFormField,
} from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState, useWatch } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFProfileSearchField from '@web/src/components/primatives/sf-profile-search-field/sf-profile-search-field'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { InfoIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import ProfileEditHeader from 'src/features/profile/profile-edit-header/profile-edit-header'
import ProfileEditMenu from 'src/features/profile/profile-edit-menu/profile-edit-menu'
import { PartnerInfo, ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import {
  BodyHairEnum,
  BuildEnum,
  DrinkingEnum,
  EducationEnum,
  EthnicEnum,
  ExperienceEnum,
  GenderEnum,
  PronounEnum,
  SexualityEnum,
  SmokingEnum,
  WeedEnum,
} from 'src/lib/types/enumTypes'
import { heightString, validateDateOfBirth } from 'src/lib/utilsClient'
import type {
  CreatePartnerInput,
  CreatePartnerMutation,
  CreatePartnerMutationVariables,
  DeletePartnerMutation,
  DeletePartnerMutationVariables,
  FindProfileEditPartnerQuery,
  FindProfileEditPartnerQueryVariables,
  UpdatePartnerInput,
  UpdatePartnerMutation,
  UpdatePartnerMutationVariables,
} from 'types/graphql'

type PartnerFormValues = {
  nickName?: string | null
  partnerId?: string | null
  profileId?: string | null
  dateOfBirth?: string | Date | null
  height: number[]
  sexuality: string
  pronouns: string
  build: string
  ethnicity: string
  education: string
  drinking: string
  weed: string
  experience: string
  smoking: string
  bodyHair: string
  gender: string
  sexualInterests: string[]
  generalInterests: string[]
  isLinkedProfile: boolean
}

const PROFILE_EDIT_PARTNER_QUERY: TypedDocumentNode<FindProfileEditPartnerQuery, FindProfileEditPartnerQueryVariables> =
  gql`
    query FindProfileEditPartnerQuery($profileId: String!) {
      profile: profile(id: $profileId) {
        ...ProfileInfo
        partners {
          ...PartnerInfo
          profile {
            ...ProfileInfo
          }
        }
      }
      interests {
        category
        id
        name
      }
    }
    ${PartnerInfo}
    ${ProfileInfo}
    ${ProfileMediaInfo}
  `

export const CREATE_PARTNER: TypedDocumentNode<CreatePartnerMutation, CreatePartnerMutationVariables> = gql`
  mutation CreatePartner($input: CreatePartnerInput!) {
    createPartner(input: $input) {
      ...PartnerInfo
    }
  }
  ${PartnerInfo}
`

export const UPDATE_PARTNER: TypedDocumentNode<UpdatePartnerMutation, UpdatePartnerMutationVariables> = gql`
  mutation UpdatePartner($id: String!, $input: UpdatePartnerInput!) {
    updatePartner(id: $id, input: $input) {
      ...PartnerInfo
    }
  }
  ${PartnerInfo}
`

export const DELETE_PARTNER: TypedDocumentNode<DeletePartnerMutation, DeletePartnerMutationVariables> = gql`
  mutation DeletePartner($id: String!) {
    deletePartner(id: $id) {
      ...PartnerInfo
    }
  }
  ${PartnerInfo}
`

const interestsToOptionsByCategory = (
  category: string,
  interests: FindProfileEditPartnerQuery['interests']
): Array<{ label: string; value: string }> =>
  (interests ?? []).filter((i) => i.category === category).map((i) => ({ label: i.name, value: i.name }))

const PartnerCard = ({
  partner,
  index,
  interests,
  onSave,
  onDelete,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: Partner type can be partial for new partners
  partner: any
  index: number
  interests: FindProfileEditPartnerQuery['interests']
  onSave?: ({ partnerId, index }: { partnerId?: string; index: number }) => void
  onDelete?: ({ partnerId, index }: { partnerId?: string; index: number }) => void
}) => {
  'use no memo' // disable React Compiler to fix issues with react hook forms

  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState(partner?.profile)
  const profileSearchRef = useRef<{ reset: () => void } | null>(null)
  const isDeletingRef = useRef(false)

  const formMethods = useForm<PartnerFormValues>({
    defaultValues: {
      nickName: partner?.nickName,
      partnerId: partner?.id,
      profileId: partner?.profileId,
      dateOfBirth: partner?.dateOfBirth ? new Date(partner.dateOfBirth).toISOString() : null,
      height: [partner?.height],
      sexuality: partner?.sexuality ?? '',
      pronouns: partner?.pronouns ?? '',
      build: partner?.build ?? '',
      ethnicity: partner?.ethnicity ?? '',
      education: partner?.education ?? '',
      drinking: partner?.drinking ?? '',
      weed: partner?.weed ?? '',
      experience: partner?.experience ?? '',
      smoking: partner?.smoking ?? '',
      bodyHair: partner?.bodyHair ?? '',
      gender: partner?.gender ?? '',
      sexualInterests: partner?.sexualInterests ?? [],
      generalInterests: partner?.generalInterests ?? [],
      isLinkedProfile: index > 0 ? !!partner?.profileId : false,
    },
  })
  const { isDirty } = useFormState({ control: formMethods.control })
  const height = useWatch({ control: formMethods.control, name: 'height' })
  const isLinkedProfile = useWatch({ control: formMethods.control, name: 'isLinkedProfile' })
  const [showSpinner, setShowSpinnerValue] = useState(false)

  useEffect(() => {
    if (isLinkedProfile) {
      formMethods.unregister('dateOfBirth')
    }
  }, [isLinkedProfile, formMethods])

  // Set the defaults when partner changes
  useEffect(() => {
    formMethods.reset({
      nickName: partner?.nickName,
      partnerId: partner?.id,
      profileId: partner?.profileId,
      dateOfBirth: partner?.dateOfBirth ? new Date(partner.dateOfBirth).toISOString() : null,
      height: [partner?.height],
      sexuality: partner?.sexuality ?? '',
      pronouns: partner?.pronouns ?? '',
      build: partner?.build ?? '',
      ethnicity: partner?.ethnicity ?? '',
      education: partner?.education ?? '',
      drinking: partner?.drinking ?? '',
      weed: partner?.weed ?? '',
      experience: partner?.experience ?? '',
      smoking: partner?.smoking ?? '',
      bodyHair: partner?.bodyHair ?? '',
      gender: partner?.gender ?? '',
      sexualInterests: partner?.sexualInterests ?? [],
      generalInterests: partner?.generalInterests ?? [],
      isLinkedProfile: index > 0 ? !!partner?.profileId : false,
    })
    setSelectedProfile(partner?.profile)
  }, [partner, formMethods, index])

  const [createPartner] = useMutation(CREATE_PARTNER, {
    onCompleted: () => {
      toast.success(t('Partner Details Created'))
      if (onSave) {
        onSave({ partnerId: partner?.id, index: index })
      }
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const [updatePartner] = useMutation(UPDATE_PARTNER, {
    onCompleted: () => {
      toast.success(t('Partner Details Updated'))
      if (onSave) {
        onSave({ partnerId: partner?.id, index: index })
      }
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const handleProfileSelected = (profile: { id: string; name: string } | null) => {
    if (profile) {
      setSelectedProfile(profile)
      formMethods.setValue('profileId', profile.id, { shouldDirty: true })
      formMethods.setValue('nickName', profile.name, { shouldDirty: true })
    } else {
      setSelectedProfile(null)
      formMethods.setValue('profileId', null, { shouldDirty: true })
    }
    return false
  }

  const onSubmit = (fieldData: PartnerFormValues) => {
    setShowSpinnerValue(true)

    const sexualInterests = Array.isArray(fieldData.sexualInterests) ? fieldData.sexualInterests : []
    const generalInterests = Array.isArray(fieldData.generalInterests) ? fieldData.generalInterests : []
    const dobRaw = fieldData.dateOfBirth
    const dateOfBirth =
      dobRaw === undefined || dobRaw === null || dobRaw === ''
        ? null
        : dobRaw instanceof Date
          ? dobRaw
          : new Date(dobRaw)

    const emptyToUndef = (v: string) => (v ? v : undefined)

    const partnerInputPayload = {
      dateOfBirth: dateOfBirth ?? undefined,
      nickName: fieldData.nickName ?? undefined,
      profileId: isLinkedProfile ? fieldData.profileId || undefined : undefined,
      height: fieldData.height?.[0],
      sexuality: emptyToUndef(fieldData.sexuality),
      pronouns: emptyToUndef(fieldData.pronouns),
      build: emptyToUndef(fieldData.build),
      ethnicity: emptyToUndef(fieldData.ethnicity),
      education: emptyToUndef(fieldData.education),
      drinking: emptyToUndef(fieldData.drinking),
      weed: emptyToUndef(fieldData.weed),
      experience: emptyToUndef(fieldData.experience),
      smoking: emptyToUndef(fieldData.smoking),
      bodyHair: emptyToUndef(fieldData.bodyHair),
      gender: emptyToUndef(fieldData.gender),
      sexualInterests,
      generalInterests,
    } as CreatePartnerInput

    const mutationInput = partnerInputPayload as UpdatePartnerInput

    if (partner.id) {
      updatePartner({
        variables: {
          id: partner.id,
          input: mutationInput,
        },
      })
    } else {
      createPartner({
        variables: {
          input: partnerInputPayload,
        },
      })
    }
  }

  return (
    <>
      <Form<PartnerFormValues> formMethods={formMethods} onSubmit={onSubmit}>
        <Card className="w-full">
          {index > 0 ? (
            <CardHeader>
              <div className="flex w-full justify-end">
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        aria-label={t('Remove Partner')}
                        onClick={() => setDeleteModal(true)}
                        type="button"
                        variant="outline"
                      />
                    }
                  >
                    <Trash2Icon className="size-4" />
                    {t('Remove Partner')}
                  </TooltipTrigger>
                  <TooltipContent side="top">{t('Delete partner information')}</TooltipContent>
                </Tooltip>
              </div>
            </CardHeader>
          ) : null}
          <CardContent className="relative pt-4">
            <div className="flex w-full flex-col gap-4">
              {index > 0 ? (
                <>
                  <div className="flex w-full justify-between">
                    <SwitchFormField
                      checkboxProps={{ id: `isLinkedProfile-${index}` }}
                      label={t("Link To a Partner's Profile")}
                      name="isLinkedProfile"
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setSelectedProfile(null)
                          formMethods.setValue('profileId', null, { shouldDirty: true })
                          profileSearchRef.current?.reset()
                        }
                      }}
                    />
                  </div>

                  {isLinkedProfile && !selectedProfile ? (
                    <Alert>
                      <InfoIcon aria-hidden className="size-4 shrink-0" />
                      <AlertDescription>
                        {t('You can only link partner profiles if you are already friends with them.')}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <Separator className="w-full bg-border" />
                </>
              ) : null}

              {isLinkedProfile ? (
                <div className="flex w-full flex-col items-start gap-4">
                  {selectedProfile ? null : (
                    <SFProfileSearchField
                      friendsOnly={true}
                      label={t('Search For Your Partner:')}
                      name="profileId"
                      onProfileSelected={handleProfileSelected}
                      ref={profileSearchRef}
                      required={!!isLinkedProfile}
                    />
                  )}

                  {selectedProfile ? (
                    <div className="w-full rounded-md border border-border p-3">
                      {/* biome-ignore lint/suspicious/noExplicitAny: Profile type compatibility */}
                      <SFProfileBanner profile={selectedProfile as any} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                  <InputFormField label={t('Partner Nick Name')} name="nickName" />

                  <div className="flex h-full min-h-0 flex-col justify-center gap-1">
                    <div className="flex w-full items-center justify-between text-sm">
                      <span>{t('Height:')}</span>
                      <span className="text-muted-foreground">{heightString(height[0])}</span>
                    </div>
                    <SliderFormField
                      name="height"
                      sliderProps={{
                        min: 61,
                        max: 250,
                      }}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <DateFormField
                      label={t('Date of Birth')}
                      name="dateOfBirth"
                      required
                      validation={{
                        validate: validateDateOfBirth,
                        required: {
                          value: true,
                          message: t('Invalid date provided'),
                        },
                      }}
                    />
                  </div>

                  <EnumSelectFormField
                    enumType={BodyHairEnum}
                    label={t('Body Hair')}
                    name="bodyHair"
                    placeholder={t('Select body hair')}
                  />
                  <EnumSelectFormField
                    enumType={BuildEnum}
                    label={t('Build')}
                    name="build"
                    placeholder={t('Select build')}
                  />
                  <EnumSelectFormField
                    enumType={DrinkingEnum}
                    label={t('Drinking')}
                    name="drinking"
                    placeholder={t('Select drinking preference')}
                  />
                  <EnumSelectFormField
                    enumType={EducationEnum}
                    label={t('Education')}
                    name="education"
                    placeholder={t('Select education level')}
                  />
                  <EnumSelectFormField
                    enumType={EthnicEnum}
                    label={t('Ethnicity')}
                    name="ethnicity"
                    placeholder={t('Select ethnicity')}
                  />
                  <EnumSelectFormField
                    enumType={ExperienceEnum}
                    label={t('Experience')}
                    name="experience"
                    placeholder={t('Select experience')}
                  />
                  <EnumSelectFormField
                    enumType={GenderEnum}
                    label={t('Gender')}
                    name="gender"
                    placeholder={t('Select gender')}
                  />
                  <EnumSelectFormField
                    enumType={PronounEnum}
                    label={t('Pronouns')}
                    name="pronouns"
                    placeholder={t('Select pronouns')}
                  />
                  <EnumSelectFormField
                    enumType={SexualityEnum}
                    label={t('Sexuality')}
                    name="sexuality"
                    placeholder={t('Select sexuality')}
                  />
                  <EnumSelectFormField
                    enumType={SmokingEnum}
                    label={t('Smoking')}
                    name="smoking"
                    placeholder={t('Select smoking preference')}
                  />
                  <EnumSelectFormField
                    enumType={WeedEnum}
                    label={t('Weed')}
                    name="weed"
                    placeholder={t('Select weed preference')}
                  />

                  <div className="col-span-1 md:col-span-2">
                    <TagMultiSelectFormField
                      label={t('Sexual Interests')}
                      name="sexualInterests"
                      options={interestsToOptionsByCategory('SEXUAL', interests)}
                      placeholder={t('Select interests')}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <TagMultiSelectFormField
                      label={t('General Interests')}
                      name="generalInterests"
                      options={interestsToOptionsByCategory('GENERAL', interests)}
                      placeholder={t('Select interests')}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <div className="mt-6 flex w-full justify-end gap-2">
          {isDirty ? (
            <Button onClick={() => formMethods.reset()} type="button" variant="outline">
              {t('Cancel')}
            </Button>
          ) : null}
          <Button disabled={!isDirty || showSpinner} type="submit">
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            {t('Save')}
          </Button>
        </div>
      </Form>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Delete')}
        onConfirm={() => {
          if (isDeletingRef.current) {
            return
          }
          isDeletingRef.current = true
          setDeleteModal(false)
          onDelete?.({ partnerId: partner?.id, index: index })
        }}
        onOpenChange={(d) => {
          if (d.open) {
            isDeletingRef.current = false
          }
          setDeleteModal(d.open)
        }}
        open={deleteModal}
        text={t('Are you sure you want to delete this partner information?')}
        title={t('Are you sure?')}
        variant="delete"
      />
    </>
  )
}

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId_/edit/partners')({
  component: EditPartnersPage,
  head: () =>
    seo({
      title: `Edit Partners | ${env.VITE_APP_NAME}`,
      description: 'Profile Edit Partners',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_EDIT_PARTNER_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function EditPartnersPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<FindProfileEditPartnerQuery>)
  const [tabValue, setTabValue] = useState('0')
  // biome-ignore lint/suspicious/noExplicitAny: Partner list can contain partial partners
  const [partnerList, setPartnerList] = useState<any[]>([])
  const inFlightDeletes = useRef(new Set<string>())

  const [deletePartner] = useMutation(DELETE_PARTNER, {
    onCompleted: () => {
      toast.success(t('Partner Deleted'))
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  // Create a refetch function that uses Apollo Client directly to avoid router reloads
  // This refetches the query without triggering TanStack Router's loader system
  const handleRefetch = async () => {
    await apolloClient.refetchQueries({ include: [PROFILE_EDIT_PARTNER_QUERY] })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading

  const { profile, interests } = data ?? {}

  useEffect(() => {
    if (profile?.partners) {
      setPartnerList(profile.partners)
    }
  }, [profile?.partners])

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return <ErrorAlert error={{ name: 'Empty', message: t('No Data') } as Error} />
  }

  const handleDeletePartner = ({ partnerId, index }: { partnerId?: string; index: number }) => {
    if (partnerId) {
      if (inFlightDeletes.current.has(partnerId)) {
        return
      }
      inFlightDeletes.current.add(partnerId)
      deletePartner({
        variables: {
          id: partnerId,
        },
      }).finally(() => {
        inFlightDeletes.current.delete(partnerId)
      })
    }

    const value = [...partnerList]
    value.splice(index, 1)
    setPartnerList(value)
    setTabValue(`${Math.max(0, value.length - 1)}`)
  }

  const handleSavePartner = () => {
    handleRefetch()
  }

  const handleAddPartner = () => {
    setPartnerList((oldValue) => {
      const next = [...oldValue, { nickName: t('Partner {{n}}', { n: oldValue.length + 1 }), height: 154 }]
      setTabValue(`${next.length - 1}`)
      return next
    })
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-0">
      <ProfileEditHeader profileId={profile.id} />
      <div className="mb-4 grid grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-2 mt-0 flex justify-center md:col-span-1 md:mt-6">
          <ProfileEditMenu className="hidden md:flex" profile={profile} w="80%" />
        </div>
        <div className="col-span-2 md:col-span-2">
          <div className="flex w-full flex-col">
            <div className="flex w-full justify-center">
              <Button disabled={partnerList.length > 5} onClick={handleAddPartner} type="button">
                {t('Add Additional Partners')}
              </Button>
            </div>

            <Tabs className="w-full pt-2" onValueChange={setTabValue} value={tabValue}>
              <TabsList className="w-full max-w-full overflow-x-auto overflow-y-hidden" variant="tabs">
                {partnerList.map((partner, idx) => {
                  return (
                    <Tooltip key={partner.id ? `tab-${partner.id}` : `tab-new-${idx}`}>
                      <TooltipTrigger render={<TabsTrigger className={cn('min-w-0 max-w-48')} value={`${idx}`} />}>
                        <span className="truncate">{partner.nickName ? partner.nickName : profile.name}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{partner.nickName ? partner.nickName : profile.name}</TooltipContent>
                    </Tooltip>
                  )
                })}
              </TabsList>

              {partnerList.map((partner, idx) => {
                return (
                  <TabsContent key={partner.id ? `card-${partner.id}` : `card-new-${idx}`} value={`${idx}`}>
                    <PartnerCard
                      index={idx}
                      interests={interests ?? []}
                      onDelete={handleDeletePartner}
                      onSave={handleSavePartner}
                      partner={partner}
                    />
                  </TabsContent>
                )
              })}
            </Tabs>
          </div>
        </div>
        <div className="col-span-1 hidden md:block" />
      </div>
    </div>
  )
}
